import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api/client';
import { ApiResponse, WhiteBoardTestListResponse, GetQbTestForWhiteBoardViewDto } from '../lib/api/types';
import { useUserStore } from '../store/useUserStore';
import { useTenantStore } from '../store/useTenantStore';
import { useLogout } from '../hooks/useLogout';
import { useLoadingState } from '../hooks/useLoadingState';
import { TestListSkeleton, ContentLoader } from '../components/LoadingStates';
import { Search, ChevronLeft, ChevronRight, LogOut, Building2, User, PenTool, FileText, Download, ClipboardList, Edit3, Target, BookOpen, HelpCircle, CheckSquare, X } from 'lucide-react';
import '../styles/animations.css';

const TestListItem: React.FC<{ 
  test: GetQbTestForWhiteBoardViewDto; 
  onOpenOptical: () => void;
  onOpenNormal: () => void;
}> = ({ test, onOpenOptical, onOpenNormal }) => {
  const isDenemeTesti = test.qbTest?.testType === 1;
  
  return (
    <div className="bg-white border-b border-gray-200 hover:bg-gray-50 transition-colors">
      {/* Mobile Layout */}
      <div className="sm:hidden">
        <div className="grid grid-cols-12 gap-2 p-3 items-center">
          {/* Test Name - Col 1-6 */}
          <div className="col-span-6">
            <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight">
              {test.qbTest?.title}
            </h3>
          </div>
          
          {/* Questions - Col 7-8 */}
          <div className="col-span-2 flex items-center justify-center">
            {test.questionCount > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <HelpCircle className="w-3 h-3" />
                <span>{test.questionCount}</span>
              </div>
            )}
          </div>
          
          {/* Booklets - Col 9-10 */}
          <div className="col-span-2 flex items-center justify-center">
            {test.qbTest?.qbTestBooklets && test.qbTest.qbTestBooklets.length > 0 && (
              <div className="flex gap-0.5">
                {test.qbTest.qbTestBooklets.map((booklet, index) => (
                  <a
                    key={booklet.id || index}
                    href={booklet.bookletUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded flex items-center justify-center transition-colors"
                  >
                    {String.fromCharCode(65 + index)}
                  </a>
                ))}
              </div>
            )}
          </div>
          
          {/* Actions - Col 11-12 */}
          <div className="col-span-2 flex gap-1 justify-end">
            <div className="group relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNormal();
                }}
                className="p-1.5 rounded bg-blue-500 hover:bg-blue-600 text-white transition-colors"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-black/90 backdrop-blur-md text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap border border-white/20 z-[60]">
                Normal
              </div>
            </div>
            
            <div className="group relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenOptical();
                }}
                className="w-8 h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white transition-colors border-2 border-orange-600 hover:border-orange-700 shadow-sm"
              >
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              </button>
              <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-black/90 backdrop-blur-md text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap border border-white/20 z-[70]">
                Optik
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="grid grid-cols-12 gap-4 p-3 items-center">
          {/* Test Name - Col 1-6 */}
          <div className="col-span-6">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {test.qbTest?.title}
            </h3>
          </div>
          
          {/* Questions - Col 7-8 */}
          <div className="col-span-2 flex items-center justify-center">
            {test.questionCount > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <HelpCircle className="w-3 h-3" />
                <span>{test.questionCount} Soru</span>
              </div>
            )}
          </div>
          
          {/* Booklets - Col 9-10 */}
          <div className="col-span-2 flex items-center justify-center">
            {test.qbTest?.qbTestBooklets && test.qbTest.qbTestBooklets.length > 0 && (
              <div className="flex gap-0.5">
                {test.qbTest.qbTestBooklets.map((booklet, index) => (
                  <a
                    key={booklet.id || index}
                    href={booklet.bookletUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded flex items-center justify-center transition-colors"
                  >
                    {String.fromCharCode(65 + index)}
                  </a>
                ))}
              </div>
            )}
          </div>
          
          {/* Actions - Col 11-12 */}
          <div className="col-span-2 flex gap-1 justify-end">
            <div className="group relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNormal();
                }}
                className="p-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-colors"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-white/90 backdrop-blur-md text-gray-900 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap border border-gray-200/50 shadow-lg z-[70]">
                <div className="absolute top-full left-4 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-white/90"></div>
                Normal Çöz
              </div>
            </div>
            
            <div className="group relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenOptical();
                }}
                className="w-8 h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white transition-colors border-2 border-orange-600 hover:border-orange-700 shadow-sm"
              >
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              </button>
              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap border border-white/20 z-[60]">
                <div className="absolute top-full right-4 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-black/80"></div>
                Optikli Çöz
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const LibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const userInfo = useUserStore((state) => state.userInfo);
  const tenantStore = useTenantStore();
  const logout = useLogout();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  // Use loading state hook for better state management
  const testsState = useLoadingState<GetQbTestForWhiteBoardViewDto[]>({
    initialLoading: true,
    minLoadingTime: 300 // Prevent flashing for fast loads
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [filter, setFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Enable scrolling for library page
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      root.style.minHeight = '100vh';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (root) {
        root.style.overflow = '';
        root.style.height = '';
        root.style.minHeight = '';
      }
    };
  }, []);

  useEffect(() => {
    fetchTests();
  }, [currentPage, pageSize, filter]);

  const fetchTests = async () => {
    const skipCount = (currentPage - 1) * pageSize;
    
    await testsState.execute(
      apiClient.getQbTestsForWhiteBoard({
        filter: filter,
        skipCount: skipCount,
        maxResultCount: pageSize
      }).then(response => {
        const data = response.data as ApiResponse<WhiteBoardTestListResponse>;

        if (data.success && data.result) {
          setTotalCount(data.result.totalCount || 0);
          return data.result.items;
        } else {
          throw new Error('Testler yüklenemedi');
        }
      })
    );
  };


  const handleTestClick = (test: GetQbTestForWhiteBoardViewDto, isOptik: boolean = true) => {
    // qbTest id'si ile whiteboard'a yönlendir
    const params = new URLSearchParams();
    params.append('qbTestId', test.qbTest.id.toString());
    if (isOptik) {
      params.append('optik', '1');
    }else{
      params.append('optik', '0');
    }
    navigate(`/whiteboard?${params.toString()}`);
  };

  const handleWhiteboardClick = () => {
    navigate('/whiteboard');
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setFilter(searchInput);
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (testsState.isLoading && testsState.isInitialLoad) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              {/* Logo */}
              <img 
                src="/tahta-icon-minimal.svg" 
                alt="Tahta" 
                className="w-8 h-8"
              />
              
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Dijital Tahta
                </h3>
                <div className="flex items-center space-x-3 text-sm text-gray-600">
                  {/* {userInfo && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {userInfo.userName}
                    </div>
                  )} */}
                  {tenantStore.formattedCode && (
                    <>
                      {/* <span className="text-gray-400">•</span>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {tenantStore.formattedCode.toUpperCase()}
                      </div> */}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              >
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold shadow-md">
                    {userInfo?.fullName?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
                </div>
              </button>
              
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-10 border border-gray-100">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{userInfo?.fullName}</p>
                    <p className="text-xs text-gray-500">{userInfo?.emailAddress}</p>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/profile');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Profilim</span>
                  </button>
                  
                  <hr className="my-2" />
                  
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      logout(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2z" />
                    </svg>
                    <span>Kurum Değiştir</span>
                    {tenantStore.formattedCode && (
                      <span className="ml-auto text-xs text-gray-400">{tenantStore.formattedCode}</span>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      logout();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Stats Cards - Desktop Only */}
      
        {/* Filter */}
        <div className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Test ara... (En az 3 karakter)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all pr-10"
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput('');
                    setFilter('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              disabled={searchInput.length < 3}
              className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 ${
                searchInput.length < 3
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow-md'
              }`}
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Ara</span>
            </button>
          </div>
          {searchInput.length > 0 && searchInput.length < 3 && (
            <p className="text-xs text-amber-600 mt-1">En az 3 karakter girmelisiniz</p>
          )}
        </div>
        {testsState.error ? (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{testsState.error?.message || String(testsState.error)}</h3>
              </div>
            </div>
          </div>
        ) : testsState.data?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Henüz test bulunmamaktadır.</p>
          </div>
        ) : (
          <div className="relative">
            {testsState.isRefreshing && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            )}
            <div className="space-y-1 mb-4">
              {testsState.data?.map((test, index) => (
                <TestListItem
                  key={test.qbTest?.id || index}
                  test={test}
                  onOpenOptical={() => handleTestClick(test, true)}
                  onOpenNormal={() => handleTestClick(test, false)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="mt-8 relative">
            <div className="absolute inset-0 bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg" />
            <div className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>
                    Toplam <span className="font-semibold text-gray-900">{totalCount}</span> test • 
                    Gösterilen: <span className="font-semibold text-gray-900">{(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalCount)}</span>
                  </span>
                </div>
                
                {/* Page Size Selector */}
                <div className="hidden sm:flex items-center gap-2">
                  <label className="text-sm text-gray-600">Sayfa başı:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value={6}>6</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                    <option value={48}>48</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 text-sm font-medium rounded-lg transition-all ${
                  currentPage === 1
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-200'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* First Page */}
              {currentPage > 3 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                  >
                    1
                  </button>
                  {currentPage > 4 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                </>
              )}

              {/* Page Numbers */}
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`min-w-[2.5rem] h-10 text-sm font-medium rounded-lg transition-all ${
                    page === currentPage
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}

              {/* Last Page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-2 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 text-sm font-medium rounded-lg transition-all ${
                  currentPage === totalPages
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-200'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              </div>
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Whiteboard Button */}
      <div className="fixed bottom-8 right-8">
        <button
          onClick={handleWhiteboardClick}
          className="bg-gray-900 hover:bg-gray-800 text-white p-3 rounded-lg shadow-lg transition-colors focus:outline-none group relative"
          title="Boş Tahta"
        >
          <FileText className="w-5 h-5" />
          <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-100 text-gray-900 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
            Yeni Boş Tahta
          </div>
        </button>
      </div>
    </div>
  );
};

export default LibraryPage;